import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { SUMMARY_PAGE_ADDRESS_FIELDS, TranslationContext } from "@is/constants";
import { CheckoutInputFields, FieldState } from "@is/types";

type Props = {
  recipientOptions?: FieldState[];
  deliveryOptions?: FieldState[];
};

const useManageDeliveryInformation = ({
  recipientOptions,
  deliveryOptions,
}: Props) => {
  const { t } = useTranslation(TranslationContext.Checkout);

  const firstAndLastName = useMemo(() => {
    const firstName = recipientOptions.find(
      (option) => option.name === CheckoutInputFields.FirstName
    );

    const lastName = recipientOptions.find(
      (option) => option.name === CheckoutInputFields.LastName
    );

    return `${firstName?.value?.length ? firstName?.value : ""} ${
      lastName?.value?.length ? lastName?.value : ""
    }`.trim();
  }, [recipientOptions]);

  const phoneNumber = useMemo(() => {
    const phone = recipientOptions.find(
      (option) => option.name === CheckoutInputFields.PhoneNumber
    );

    return phone?.value;
  }, [recipientOptions]);

  const email = useMemo(() => {
    const email = recipientOptions.find(
      (option) => option.name === CheckoutInputFields.Email
    );

    return email?.value;
  }, [recipientOptions]);

  const phoneOrEmail = useMemo(() => {
    const phoneOrEmailField = recipientOptions.find(
      (option) => option.name === CheckoutInputFields.PhoneNumberOrEmail
    );

    return phoneOrEmailField?.value;
  }, [recipientOptions]);

  const address = useMemo(() => {
    const addressFields = deliveryOptions.filter((option) =>
      SUMMARY_PAGE_ADDRESS_FIELDS.includes(option.name as CheckoutInputFields)
    );

    const fieldsWithValue = addressFields.filter((option) => option.value);

    return fieldsWithValue.map((option) => option.value).join(", ");
  }, [deliveryOptions]);

  const location = useMemo(() => {
    const locationFields = deliveryOptions.filter(
      (option) =>
        !SUMMARY_PAGE_ADDRESS_FIELDS.includes(
          option.name as CheckoutInputFields
        )
    );

    const fieldsWithValue = locationFields.filter((option) => option.value);

    return fieldsWithValue.map((option) => option.value).join(", ");
  }, [deliveryOptions]);

  const hasPersonalInformation = useMemo(
    () =>
      !!firstAndLastName?.length ||
      !!phoneNumber?.length ||
      !!email?.length ||
      !!phoneOrEmail?.length,
    [email, firstAndLastName, phoneNumber, phoneOrEmail]
  );

  const hasDeliveryInformation = useMemo(
    () => !!address?.length || !!location?.length,
    [address, location]
  );

  return {
    t,
    hasPersonalInformation,
    hasDeliveryInformation,
    firstAndLastName,
    phoneNumber,
    email,
    phoneOrEmail,
    address,
    location,
  };
};

export default useManageDeliveryInformation;
